import cn from "classnames";
import { FC, ReactNode, useRef, useState } from "react";
import { useInputState } from "../hooks/useInputState";

export type FloatingLabelOptions = {
  label?: ReactNode;
  classNames?: {
    label?: string;
    floating?: string;
  };
  float?: boolean;
  blurOnChange?: boolean;
};

export function withFloatingLabel<
  T extends {
    value?: any;
    defaultValue?: any;
    onChange?: (val: any) => void;
    className?: string;
    onFocus?: (e: any) => void;
    onBlur?: (e: any) => void;
  } & object,
>(WrappedComponent: React.ComponentType<T>) {
  type WrappedProps = Omit<T, "value" | "onChange" | "onFocus" | "onBlur" | "className">;

  type Props = WrappedProps &
    FloatingLabelOptions & {
      value?: T["value"];
      defaultValue?: T["value"];
      onChange?: T["onChange"];
      className?: string;
      onFocus?: T["onFocus"];
      onBlur?: T["onBlur"];
    };

  const ComponentWithFloatingLabel: FC<Props> = ({ label, classNames, value, defaultValue, onChange, onFocus, onBlur, float, blurOnChange, ...props }) => {
    const [focused, setFocused] = useState(false);

    const [currentValue, setValue] = useInputState<string>({
      value,
      defaultValue,
      onChange,
    });

    const isFloating = float || focused || Boolean(currentValue);
    const ref = useRef<HTMLDivElement | null>(null);
    return (
      <div className="relative w-full">
        <WrappedComponent
          ref={ref}
          value={currentValue}
          onFocus={(e: any) => {
            setFocused(true);
            onFocus?.(e);
          }}
          onBlur={(e: any) => {
            setFocused(false);
            onBlur?.(e);
          }}
          onChange={(val: any) => {
            const v = val.target?.value ?? val;
            setValue(v);
            if (blurOnChange !== false) {
              setFocused(false);
              ref.current?.blur();
            }
          }}
          placeholder=" "
          {...(props as any)}
          className={props.className}
        />

        <label
          className={cn(
            "pointer-events-none absolute start-0 z-10 ps-2 transition-all duration-200",
            classNames?.label,
            isFloating ? cn("top-1 text-xs", classNames?.floating) : cn("top-1/2 -translate-y-1/2"),
          )}
        >
          {label}
        </label>
      </div>
    );
  };

  return ComponentWithFloatingLabel;
}
